import { create } from 'zustand';
import type { PatientNote, PaginatedPatientNotes } from '@/types/patientNotes';
import { apiService } from '@/services/ApiService';
import { PatientNotesApiError } from '@/types/patientNotes';

type PatientNotesState = {
  loading: boolean;
  error: string | null;
  submitError: string | null;
  submitting: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  items: PatientNote[];
  draft: string;
  lastDietitianId?: string;
  lastPatientId?: string;
};

type PatientNotesActions = {
  load: (dietitianId: string, patientId: string, page?: number, pageSize?: number) => Promise<void>;
  refresh: () => Promise<void>;
  addNote: (dietitianId: string, patientId: string, content: string) => Promise<PatientNote | undefined>;
  updateNote: (
    dietitianId: string,
    patientId: string,
    noteId: string,
    content: string
  ) => Promise<PatientNote | undefined>;
  setDraft: (content: string) => void;
  reset: () => void;
};

const initialState: PatientNotesState = {
  loading: false,
  error: null,
  submitError: null,
  submitting: false,
  page: 1,
  pageSize: 10,
  totalItems: 0,
  items: [],
  draft: '',
  lastDietitianId: undefined,
  lastPatientId: undefined,
};

export const usePatientNotesStore = create<PatientNotesState & PatientNotesActions>((set, get) => ({
  ...initialState,

  load: async (dietitianId, patientId, page = 1, pageSize = get().pageSize) => {
    set((state) => ({
      ...state,
      loading: true,
      error: null,
      lastDietitianId: dietitianId,
      lastPatientId: patientId,
      pageSize,
    }));

    try {
      const result: PaginatedPatientNotes = await apiService.patientNotes.listNotes({
        dietitianId,
        patientId,
        page,
        pageSize,
      });

      set((state) => ({
        ...state,
        loading: false,
        error: null,
        page: result.page,
        pageSize: result.pageSize,
        totalItems: result.totalItems,
        items: result.items,
      }));
    } catch (err) {
      const message =
        err instanceof PatientNotesApiError ? err.message : 'Unable to load patient notes. Please try again.';
      set((state) => ({
        ...state,
        loading: false,
        error: message,
        items: [],
        totalItems: 0,
      }));
    }
  },

  refresh: async () => {
    const { lastDietitianId, lastPatientId, page, pageSize, load } = get();
    if (!lastDietitianId || !lastPatientId) return;
    await load(lastDietitianId, lastPatientId, page, pageSize);
  },

  addNote: async (dietitianId, patientId, content) => {
    const trimmed = content.trim();
    if (!trimmed) {
      set((state) => ({
        ...state,
        submitError: 'Note cannot be empty.',
      }));
      return undefined;
    }
    if (trimmed.length > 10_000) {
      set((state) => ({
        ...state,
        submitError: 'Note cannot exceed 10,000 characters.',
      }));
      return undefined;
    }

    set((state) => ({
      ...state,
      submitting: true,
      submitError: null,
    }));

    try {
      const note: PatientNote = await apiService.patientNotes.createNote({
        dietitianId,
        patientId,
        contentMarkdown: trimmed,
      });

      set((state) => {
        const updatedItems = [note, ...state.items].slice(0, state.pageSize);
        return {
          ...state,
          submitting: false,
          submitError: null,
          draft: '',
          page: 1,
          items: updatedItems,
          totalItems: state.totalItems + 1,
          lastDietitianId: dietitianId,
          lastPatientId: patientId,
        };
      });

      return note;
    } catch (err) {
      const message =
        err instanceof PatientNotesApiError ? err.message : 'Unable to save patient note. Please try again.';
      set((state) => ({
        ...state,
        submitting: false,
        submitError: message,
      }));
      return undefined;
    }
  },

  updateNote: async (dietitianId, patientId, noteId, content) => {
    const trimmed = content.trim();
    if (!trimmed) {
      set((state) => ({
        ...state,
        submitError: 'Note cannot be empty.',
      }));
      return undefined;
    }
    if (trimmed.length > 10_000) {
      set((state) => ({
        ...state,
        submitError: 'Note cannot exceed 10,000 characters.',
      }));
      return undefined;
    }

    set((state) => ({
      ...state,
      submitting: true,
      submitError: null,
    }));

    try {
      const updated: PatientNote = await apiService.patientNotes.updateNote({
        dietitianId,
        patientId,
        noteId,
        contentMarkdown: trimmed,
      });

      set((state) => {
        const updatedItems = state.items.map((item) => (item.id === noteId ? updated : item));
        return {
          ...state,
          submitting: false,
          submitError: null,
          items: updatedItems,
        };
      });

      return updated;
    } catch (err) {
      const message =
        err instanceof PatientNotesApiError ? err.message : 'Unable to update patient note. Please try again.';
      set((state) => ({
        ...state,
        submitting: false,
        submitError: message,
      }));
      return undefined;
    }
  },

  setDraft: (content) =>
    set((state) => ({
      ...state,
      draft: content,
    })),

  reset: () => set(() => ({ ...initialState })),
}));
